/**
 * Scenario Entity
 *
 * A pure domain entity representing an ATC audio scenario.
 * Contains business rules and invariants - no framework or persistence dependencies.
 */

export interface Scenario {
  readonly id: string;
  readonly cdnUrl: string;
  readonly sizeBytes: number;
  readonly uploadedAt: Date;
}

/**
 * Get the size of a scenario in megabytes.
 */
export function getSizeMB(scenario: Scenario): number {
  return scenario.sizeBytes / (1024 * 1024);
}

/**
 * Get a human-readable size string for a scenario.
 */
export function getFormattedSize(scenario: Scenario): string {
  const mb = getSizeMB(scenario);
  if (mb < 1) {
    const kb = scenario.sizeBytes / 1024;
    return `${kb.toFixed(1)} KB`;
  }
  return `${mb.toFixed(1)} MB`;
}

/**
 * Check if a scenario is valid (has all required fields).
 */
export function isValidScenario(scenario: Partial<Scenario>): scenario is Scenario {
  return Boolean(
    scenario.id &&
      scenario.cdnUrl &&
      typeof scenario.sizeBytes === 'number' &&
      scenario.uploadedAt instanceof Date
  );
}

/**
 * Create a new Scenario entity.
 * Throws if required fields are missing.
 */
export function createScenario(input: {
  id: string;
  cdnUrl: string;
  sizeBytes: number;
  uploadedAt: Date | string;
}): Scenario {
  if (!input.id || !input.cdnUrl || typeof input.sizeBytes !== 'number') {
    throw new Error('Scenario is missing required fields');
  }

  const uploadedAt =
    input.uploadedAt instanceof Date
      ? input.uploadedAt
      : new Date(input.uploadedAt);

  return {
    id: input.id,
    cdnUrl: input.cdnUrl,
    sizeBytes: input.sizeBytes,
    uploadedAt,
  };
}
